import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { getTaskLabel } from '../../task-labels';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { CaCorpIncorporationTask } from '../config';

export const ApproveCompanyDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const router = useRouter();
  const { role, serviceRequest } = context;

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  const taskWithWorkRequest = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.FILL_CORPORATION_DETAILS
    );

  // Sibling fill task is the fixer; issues reported from this review task clear on its completion.
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    taskWithWorkRequest,
    task
  );

  const navigateToVerifyWorkRequest = () => {
    router.push(
      `/work-request/${taskWithWorkRequest?.workRequestId}/verify/CA`
    );
  };

  const getLabel = (role: TaskParty, status: TaskStatus) =>
    getTaskLabel(
      'EM_INC_CA',
      task.legacyServiceRequestTaskTaskId,
      role,
      status,
      task.title
    ) ?? task.title;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) =>
      ServiceRequestTaskIconConfig[hasOpenReportedIssue ? TaskStatus.IN_PROGRESS : status],
    getStateConfig: (state) => {
      if (hasOpenReportedIssue) {
        const issueMap = {
          [TaskParty.CLIENT]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="We will review your questionnaire after you resolve the current issues."
                className="whitespace-pre-line"
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskParty.AGENT]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          },
        };
        const issueConfig = issueMap[state.role];
        if (issueConfig) return issueConfig;
      }

      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.COMPLETED)}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.BACKLOG)}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                variant="primary"
                onClick={navigateToVerifyWorkRequest}
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="primary"
                onClick={navigateToVerifyWorkRequest}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.IN_PROGRESS)}
                status={TaskStatus.IN_PROGRESS}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.BACKLOG)}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={navigateToVerifyWorkRequest}
                >
                  View <Icons.ArrowNarrowRight className="w-4" />
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.COMPLETED)}
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{ role, status: task.status }}
      config={config}
      context={context}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
